import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { LoadDiskAdminOverviewTask } from '../Tasks/LoadDiskAdminOverviewTask'
import { LoadDiskAdminTrendTask } from '../Tasks/LoadDiskAdminTrendTask'
import { LoadDiskOffboardingsTask } from '../Tasks/LoadDiskOffboardingsTask'

import type { DiskAdminOverview, DiskAdminTrend, DiskAdminTrendDays, DiskOffboardingRow } from '../Contracts/DiskAdmin'

/** Скільки справ звільнення показує огляд (решта — «N із M»). */
export const OVERVIEW_OFFBOARDINGS = 5

/**
 * Огляд компанії (`DISK-006` §3.1): агрегати, тренд і справи звільнення —
 * три незалежні запити; помилка одного не гасить решту блоків.
 */
export class DiskAdminOverviewStore {
    overview: DiskAdminOverview | null = null
    isLoading = false
    error: string | null = null

    trendDays: DiskAdminTrendDays = 90
    trend: DiskAdminTrend | null = null
    isTrendLoading = false
    trendError: string | null = null

    offboardings: DiskOffboardingRow[] = []
    offboardingsTotal = 0

    private trendRequest = 0

    constructor() {
        makeAutoObservable<DiskAdminOverviewStore, 'trendRequest'>(this, { trendRequest: false })
    }

    load(): void {
        void this.loadOverview()
        void this.loadTrend()
        void this.loadOffboardings()
    }

    async loadOverview(): Promise<void> {
        this.isLoading = true
        this.error = null

        try {
            const overview = await LoadDiskAdminOverviewTask()

            runInAction(() => {
                this.overview = overview
            })
        } catch (error) {
            runInAction(() => {
                this.error = serverMessage(error, 'Не вдалося завантажити огляд диска')
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    setTrendDays(days: DiskAdminTrendDays): void {
        if (days === this.trendDays) return
        this.trendDays = days
        void this.loadTrend()
    }

    async loadTrend(): Promise<void> {
        const id = ++this.trendRequest

        this.isTrendLoading = true
        this.trendError = null

        try {
            const trend = await LoadDiskAdminTrendTask(this.trendDays)

            runInAction(() => {
                if (id === this.trendRequest) this.trend = trend
            })
        } catch (error) {
            runInAction(() => {
                if (id === this.trendRequest) this.trendError = serverMessage(error, 'Не вдалося завантажити тренд')
            })
        } finally {
            runInAction(() => {
                if (id === this.trendRequest) this.isTrendLoading = false
            })
        }
    }

    async loadOffboardings(): Promise<void> {
        try {
            const result = await LoadDiskOffboardingsTask(OVERVIEW_OFFBOARDINGS)

            runInAction(() => {
                this.offboardings = result.items
                this.offboardingsTotal = result.total
            })
        } catch {
            // Блок справ — додатковий: лічильник у KPI береться з огляду
            runInAction(() => {
                this.offboardings = []
                this.offboardingsTotal = 0
            })
        }
    }
}

export const diskAdminOverviewStore = new DiskAdminOverviewStore()
